import { getCantMissRegistry } from './cantMissRegistry.js';
import { projectDdxKnowledgeForSession } from './projectDdxKnowledge.js';
import { CHIEF_COMPLAINT_RULES } from './ddxChiefComplaintRules.js';

/**
 * @param {string} text
 * @returns {string | null} chief complaint registry id
 */
export function matchChiefComplaintIdFromText(text) {
  if (!text || typeof text !== 'string') return null;
  for (const rule of CHIEF_COMPLAINT_RULES) {
    for (const re of rule.patterns) {
      if (re.test(text)) return rule.id;
    }
  }
  return null;
}

/**
 * Format can’t-miss registry entry as a short clinician-facing checklist string.
 * @param {Object} entry
 * @returns {string}
 */
export function formatCantMissEntryAsSnippet(entry) {
  if (!entry?.requiredDiagnoses?.length) return '';
  const lines = entry.requiredDiagnoses.map((d) => `• ${d.name} — ${d.rationale}`);
  const mim = (entry.requiredMimics || []).map((m) => `Mimics: ${m.name} (vs ${(m.mimics || []).join(', ')})`);
  return [`Chief complaint: ${entry.chiefComplaint}`, 'Can’t-miss diagnoses:', ...lines, ...(mim.length ? ['', ...mim] : [])].join(
    '\n'
  );
}

/**
 * Build optional appendix for LLM user prompt (transcript context). Returns empty string if no match.
 * @param {string} transcriptText
 * @param {{ maxApproxChars?: number }} [options]
 * @returns {string}
 */
export function getCantMissGroundingAppendix(transcriptText, options = {}) {
  const id = matchChiefComplaintIdFromText(transcriptText);
  if (!id) return '';
  const entry = getCantMissRegistry(id);
  if (!entry) return '';
  const snippet = formatCantMissEntryAsSnippet(entry);
  const { text } = projectDdxKnowledgeForSession(
    { chiefComplaintId: id, cantMissSnippet: snippet },
    { maxApproxChars: options.maxApproxChars ?? 6000 }
  );
  if (!text) return '';
  return `\n\n---\n[DDX-PLUS: curated can’t-miss checklist for "${entry.chiefComplaint}" — not a diagnosis; use clinical judgment.]\n${text}\n---\n`;
}
